import React, { ChangeEvent, FC, FormEvent, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AgreementCheckbox } from 'components/AgreementCheckbox';
import { Loader } from 'components/Loader';
import { BrandPayButton } from 'components/BrandPayButton';
import { ReactComponent as StarIcon } from 'assets/images/star.svg';
import i18n from 'i18next';
import { useCountryContext } from 'context/CountryProvider';
import { COMMENT_LENGTH, NUMBER_REGEXP, PAYER_EMAIL_LENGTH, PAYER_NAME_LENGTH } from 'common/config';
import { EEventAction, EFunnelSource, EPaymentMethod, EYandexMetrikaEvent } from 'common/const';
import { usePayerContact } from 'common/hooks/usePayerContact';
import { usePageRestore } from 'common/hooks/usePageRestore';
import { isFullName, normalizeSpaces } from 'common/helpers/payerContact.helper';
import {
  getDefaultPresetValue,
  getAmountErrorText,
  getPresetOptions,
  getRatingOptions,
  getServiceOptions,
  getTipsAgreementItems,
} from 'common/helpers/tips.helper';
import {
  isFractionalAmountEnabled,
  parseAmountInput,
  prepareAmountForSubmit,
  sanitizeFractionalAmountInput,
} from 'common/helpers/amount.helper';
import { getPayBrand, getPayBrandMethod, isPayBrandEnabled } from 'common/helpers/payBrand.helper';
import { getHiddenFields } from 'common/helpers/paymentForm.helper';
import { forgetSavedCard } from 'common/helpers/savedCard.helper';
import { sendYandexMetrikaEvent } from 'common/helpers/yandex.helper';
import { getFunnelCurrency, getFunnelParams, saveCheckoutSnapshot } from 'common/helpers/metrika.helper';
import { IUser } from 'common/types/user.types';
import { IOrganization } from 'common/types/organization.types';
import { IPaymentFormData, ITipsFormData, ITipsFormErrors, TAmountError } from 'common/types/tips.types';
import { useService } from 'common/hooks/useService';
import { getEventPayload } from 'common/helpers/event.helper';

interface IComponentProps {
  visitorUid: string | null;
  user: IUser | null;
  organization: IOrganization | null;
  /** Источник перехода для событий воронки (metrika-funnel, R-1, R-3). */
  source: EFunnelSource;
}

export const Tips: FC<IComponentProps> = ({ visitorUid, user, organization, source }) => {
  const [formData, setFormData] = useState<ITipsFormData>({
    amount: '',
    visitor_uid: visitorUid ? visitorUid : '',
    terms_and_conditions: false,
    pay_commission_instead_of_waiter: false,
    uuid: '',
  });
  const [formErrors, setFormErrors] = useState<ITipsFormErrors>({});
  const [preset, setPreset] = useState<number | null>(null);
  const [paymentData, setPaymentData] = useState<IPaymentFormData | null>(null);
  // Ожидание start и ухода на форму: все кнопки оплаты заблокированы, виден индикатор (R-10)
  const [pending, setPending] = useState<boolean>(false);
  // Нажатая кнопка: click приходит раньше submit той же формы (R-2)
  const paymentMethodRef = useRef<EPaymentMethod>(EPaymentMethod.Card);
  const amountRef = useRef<HTMLDivElement | null>(null);
  const nameInputRef = useRef<HTMLInputElement | null>(null);
  const { t } = useTranslation();
  const { Service } = useService();
  // Контакты гостя: подстановка из куки API и старых ключей, по клику на устройстве ничего не пишется (payer-form)
  const { contact, changeContact, forgetLegacyContact } = usePayerContact(Service.getPayerContact);
  // Подписи сумм, комиссии и согласие — по действующей стране (US-034, R-3)
  const { country } = useCountryContext();

  const presetOptions = getPresetOptions(preset, country, organization);
  const ratingOptions = getRatingOptions(formData.rating);
  const serviceOptions = getServiceOptions(t, formData);
  const agreementItems = getTipsAgreementItems(t, formData, country, organization);
  const submitDisabled = !formData.terms_and_conditions || !formData.uuid.length || pending;
  // ME — дробный режим поля суммы; RS и неизвестная страна — сегодняшний целочисленный (US-023, И1)
  const fractionalAmount = isFractionalAmountEnabled(organization);
  // Кнопка Apple Pay / Google Pay — только сербская организация, бренд по устройству (R-1, R-6)
  const payBrand = isPayBrandEnabled(organization) ? getPayBrand() : null;
  const eventPayload = getEventPayload(formData.visitor_uid, user?.id);
  // Общие параметры воронки; без заведения события формы не уходят (metrika-funnel, R-4)
  const funnelParams = organization ? getFunnelParams(organization, source) : null;
  const currency = getFunnelCurrency(organization);

  const sendAmountSelected = (amountSource: 'preset' | 'custom', amount: number) => {
    if (funnelParams) {
      sendYandexMetrikaEvent(EYandexMetrikaEvent.TipAmountSelected, {
        ...funnelParams,
        amount_source: amountSource,
        amount,
        currency,
      });
    }
  };

  /* Уход из поля своей суммы: прежняя цель всегда; tipAmountSelected - только своя сумма (не пресет)
   *  с непустым числом после разбора поля (R-4). */
  const onAmountBlur = () => {
    sendYandexMetrikaEvent(EYandexMetrikaEvent.ClickTipValue);

    const amount = parseAmountInput(formData.amount);

    if (preset === null && formData.amount.trim() !== '' && Number.isFinite(amount)) {
      sendAmountSelected('custom', amount);
    }
  };

  /* Платёж создан: событие и снимок параметров последней начатой попытки для экрана «спасибо»
   *  и продолжения после отмены (metrika-landing, R-4). Номер платежа - из ответа start; ответ без номера -
   *  событие и снимок без него. Сумма - пресет, если выбран пресет (нажатый или предвыбранный). */
  const sendPaymentStarted = (paymentId: number | null) => {
    if (!funnelParams || !formData.uuid) {
      return;
    }

    const params = {
      ...funnelParams,
      ...(paymentId !== null ? { payment_id: paymentId } : {}),
      payment_method: paymentMethodRef.current,
      amount_source: preset !== null ? ('preset' as const) : ('custom' as const),
      currency,
      guest_covers_commission: formData.pay_commission_instead_of_waiter,
    };

    saveCheckoutSnapshot({ ...params, uuid: formData.uuid, started_at: Date.now() });
    sendYandexMetrikaEvent(EYandexMetrikaEvent.PaymentStarted, params);
  };

  const onPresetChange = (e: ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;

    setPreset(Number(value));
    setFormData((prev) => ({ ...prev, amount: value }));
    setFormErrors({});
    sendAmountSelected('preset', Number(value));
  };

  const onAmountChange = (e: ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;

    setPreset(null);
    setFormData((prev) => ({
      ...prev,
      amount: fractionalAmount ? sanitizeFractionalAmountInput(value, prev.amount) : value,
    }));
    setFormErrors({});
  };

  const onNameChange = (e: ChangeEvent<HTMLInputElement>) => {
    changeContact('name', e.target.value);
    setFormErrors((prev) => ({ ...prev, name: false }));
  };

  const onEmailChange = (e: ChangeEvent<HTMLInputElement>) => {
    changeContact('email', e.target.value);
  };

  const onRatingChange = (e: ChangeEvent<HTMLInputElement>) => {
    sendYandexMetrikaEvent(EYandexMetrikaEvent.SetRatingStar);
    setFormData((prev) => ({ ...prev, rating: Number(e.target.value) }));
  };

  const onServiceChange = (name: string, checked: boolean) => {
    setFormData((prev) => ({ ...prev, [name]: checked }));
  };

  const onCommentChange = (e: ChangeEvent<HTMLTextAreaElement>) => {
    setFormData((prev) => ({ ...prev, text_comment: e.target.value }));
  };

  const onAgreementChange = (name: string, value: boolean) => {
    if (name === 'terms_and_conditions') {
      sendYandexMetrikaEvent(EYandexMetrikaEvent.CheckboxCode289);
    }

    if (name === 'pay_commission_instead_of_waiter' && funnelParams) {
      sendYandexMetrikaEvent(EYandexMetrikaEvent.CommissionToggleClicked, { ...funnelParams, guest_covers_commission: value });
    }

    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const validateForm = () => {
    const errors: ITipsFormErrors = {};

    const amountError = getAmountError();

    if (amountError) {
      errors.amount = amountError;

      amountRef.current?.scrollIntoView({
        behavior: 'smooth',
        block: 'center',
      });
    }

    // «Имя Фамилия» - два слова (R-2); фокус на поле, если сумма в порядке
    if (!isFullName(contact.name)) {
      errors.name = true;

      if (!errors.amount) {
        nameInputRef.current?.focus();
      }
    }

    setFormErrors(errors);

    return Object.keys(errors).length === 0;
  };

  /* Зеркало серверной проверки диапазона (payment-method-rename, R-13): пусто или ниже минимума — 'min';
   *  выше максимума страны — 'max', сравнивается сумма, которая уйдёт в start; ровно максимум проходит. */
  const getAmountError = (): TAmountError | undefined => {
    const amount = parseAmountInput(formData.amount);

    if (!amount || (organization?.min_amount && organization.min_amount > amount)) {
      return 'min';
    }

    if (organization?.max_amount && prepareAmountForSubmit(amount, organization) > organization.max_amount) {
      return 'max';
    }

    return undefined;
  };

  const onPayClick = (paymentMethod: EPaymentMethod) => {
    paymentMethodRef.current = paymentMethod;
    // Способ оплаты в событии pay: статистика по кнопкам (payment-method-rename, R-9)
    Service.sendEvent({ ...eventPayload, action: EEventAction.Pay, extra: { payment_method: paymentMethod } });
    sendYandexMetrikaEvent(EYandexMetrikaEvent.ClickPaymentButton);
  };

  const onSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    if (organization?.tips_disabled) {
      alert(t('payment.tips.askForHvalaTerminal'));
      return;
    }

    if (pending || !validateForm()) {
      return;
    }

    setPending(true);

    const response = await Service.submitPaymentForm({
      ...formData,
      ...contact,
      // Имя уходит без повторных пробелов: как в куку и письмо, так и в metadata платежа
      name: normalizeSpaces(contact.name),
      amount: prepareAmountForSubmit(parseAmountInput(formData.amount), organization),
      payment_method: paymentMethodRef.current,
      language: i18n.resolvedLanguage,
    });

    // Отказ: сообщение показал Service, форма снова рабочая (R-10, F-19)
    if (!response) {
      setPending(false);
      return;
    }

    // Платёж создан (200): API поставил куку с введёнными контактами, старые ключи больше не нужны (R-5).
    // Кнопки остаются заблокированы до ухода на форму банка или PaySpot (R-10)
    forgetLegacyContact();
    sendPaymentStarted(response.paymentId);
    // В форму провайдера уходят только поля data: номер платежа лежит рядом и в форму не попадает
    setPaymentData(response.form);
  };

  // Возврат «назад» из back/forward cache: экран в состоянии ухода, форма уже отправлена.
  // Снимаем блокировку и старую форму, start повторно не шлём (R-10, walkthrough шаг 11)
  usePageRestore(() => {
    setPending(false);
    setPaymentData(null);
  });

  // Сохранённой карты больше нет: токен и флаг с прошлых оплат удаляются (R-9)
  useEffect(() => {
    forgetSavedCard();
  }, []);

  useEffect(() => {
    if (user && user.uuid) {
      setFormData((prev) => ({ ...prev, uuid: user.uuid as string }));
    }
  }, [user]);

  useEffect(() => {
    const defaultPreset = getDefaultPresetValue(organization);

    if (defaultPreset) {
      setPreset(defaultPreset);
      setFormData((prev) => ({ ...prev, amount: defaultPreset.toString() }));
    }
  }, [organization]);

  useEffect(() => {
    if (paymentData !== null) {
      const btn: HTMLElement | null = document.getElementById('submitPaymentFormBtn');

      if (btn) {
        btn.click();
      }
    }
  }, [paymentData]);

  return (
    <>
      <form className="tips" onSubmit={onSubmit}>
        {!!presetOptions?.length && (
          <div className="tips__presets">
            <div>{t('payment.tips.selectTipAmount')}</div>

            <div className="tips__presets-scroll">
              {presetOptions.map(({ id, label, value }) => {
                const checked = value === preset;

                return (
                  <div key={id} className="tips__presets-item">
                    <input id={id} type="radio" name="preset" value={value} checked={checked} onChange={onPresetChange} />

                    <label htmlFor={id}>{label}</label>
                  </div>
                );
              })}
            </div>

            <div className="tips__presets-divider">{t('payment.tips.or')}</div>
          </div>
        )}

        <div className="tips__amount" ref={amountRef}>
          {/* ME: текстовый ввод с decimal-клавиатурой, маска в onChange (покрывает и вставку —
              клавишный фильтр стал бы вторым источником правды). RS / страна неизвестна:
              сегодняшние атрибуты и обработчики байт-в-байт (И1). */}
          {fractionalAmount ? (
            <input
              name="amount"
              value={formData.amount}
              onChange={onAmountChange}
              className={`input ${formErrors.amount ? 'error' : ''}`}
              type="text"
              inputMode="decimal"
              placeholder={t('payment.tips.amountPlaceholder')}
              onBlur={onAmountBlur}
            />
          ) : (
            <input
              name="amount"
              value={formData.amount}
              onChange={onAmountChange}
              className={`input ${formErrors.amount ? 'error' : ''}`}
              type="number"
              placeholder={t('payment.tips.amountPlaceholder')}
              onKeyDown={(e) => {
                const regexp = NUMBER_REGEXP;

                if (!regexp.test(e.key) && e.key !== 'Backspace') {
                  e.preventDefault();
                }
              }}
              onBlur={onAmountBlur}
            />
          )}
          {formErrors.amount && (
            <div className="tips__amount-error-text">{getAmountErrorText(t, country, formErrors.amount, organization)}</div>
          )}
        </div>

        <div className="tips__sender-data">
          <h2>{t('payment.tips.transactionNotification')}</h2>
          {/* Автозаполнение браузера: полное имя и email (R-1); длиннее лимита поле не принимает (R-2).
              Email без type="email": формат, как сегодня, не проверяется, только обязательность. */}
          <div className="sender-data">
            <div className="sender-data__form-group">
              <label htmlFor="payer-name">{t('payment.tips.fullNameLabel')}</label>
              <input
                id="payer-name"
                ref={nameInputRef}
                name="name"
                value={contact.name}
                onChange={onNameChange}
                className={`input ${formErrors.name ? 'error' : ''}`}
                autoComplete="name"
                maxLength={PAYER_NAME_LENGTH}
                required
                aria-invalid={!!formErrors.name}
                aria-describedby={formErrors.name ? 'payer-name-error' : undefined}
                placeholder={t('payment.tips.fullNamePlaceholder')}
              />
              {formErrors.name && (
                <div id="payer-name-error" className="sender-data__error-text">
                  {t('payment.tips.fullNameError')}
                </div>
              )}
            </div>
            <div className="sender-data__form-group">
              <label htmlFor="payer-email">{t('payment.tips.emailLabel')}</label>
              <input
                id="payer-email"
                name="email"
                value={contact.email}
                onChange={onEmailChange}
                className="input"
                type="text"
                inputMode="email"
                autoComplete="email"
                autoCapitalize="none"
                spellCheck={false}
                maxLength={PAYER_EMAIL_LENGTH}
                required
                placeholder={t('payment.tips.emailPlaceholder')}
              />
            </div>
          </div>
          <p>{t('payment.tips.promise')}</p>
        </div>

        {organization?.send_feedback && (
          <>
            <div className="tips__rating">
              <div>{t('payment.tips.rateTheService')}</div>

              <div className="tips__rating-container">
                {ratingOptions.map(({ id, value, checked, isFilled }) => {
                  return (
                    <div key={id} className="tips__rating-item">
                      <input id={id} type="radio" name="rating" value={value} checked={checked} onChange={onRatingChange} />

                      <label htmlFor={id}>
                        <StarIcon className={`${isFilled ? 'active' : ''}`} />
                      </label>
                    </div>
                  );
                })}
              </div>
            </div>

            <div className={`tips__services ${formData.rating ? 'visible' : ''}`}>
              <div>{t('payment.tips.especiallyLike')}</div>

              <div className="tips__services-container">
                {serviceOptions.map(({ label, icon, name, checked }) => {
                  return (
                    <div key={name} className="tips__services-item">
                      <input
                        id={name}
                        name={name}
                        type="checkbox"
                        value={`${checked}`}
                        onChange={(e) => onServiceChange(name, e.target.checked)}
                      />

                      <label htmlFor={name}>
                        <span>{icon}</span>

                        <span>{label}</span>
                      </label>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* comment-after-rating (R-1): комментарий, как и «что понравилось», появляется после выбора звёзд */}
            <div className={`tips__comment ${formData.rating ? 'visible' : ''}`}>
              <div>{t('payment.tips.yourFeedback')}</div>

              <textarea
                name="tips__comment"
                className="input"
                placeholder={t('payment.tips.commentPlaceholder')}
                rows={3}
                value={formData.text_comment}
                onChange={onCommentChange}
                maxLength={COMMENT_LENGTH}
                tabIndex={formData.rating ? undefined : -1}
              />
            </div>
          </>
        )}

        <div className="tips__btns-container">
          <div className="btn btn-submit">
            <button type="submit" onClick={() => onPayClick(EPaymentMethod.Card)} disabled={submitDisabled}>
              {t('payment.tips.paymentByCard')}
            </button>
          </div>

          {payBrand && (
            <BrandPayButton brand={payBrand} onClick={() => onPayClick(getPayBrandMethod(payBrand))} disabled={submitDisabled} />
          )}
        </div>

        <div className="tips__agreements">
          {agreementItems.map(({ label, name, checked }, index) => {
            return <AgreementCheckbox key={index} label={label} name={name} checked={checked} onChange={onAgreementChange} />;
          })}
        </div>
      </form>

      {pending && <Loader />}

      {paymentData !== null && (
        <form action={paymentData.Url} method="post">
          {getHiddenFields(paymentData).map(([key, value]) => (
            <input type="hidden" name={key} value={value} key={key} />
          ))}
          <input id="submitPaymentFormBtn" type="submit" value="Pay Tips" />
        </form>
      )}
    </>
  );
};
